import React, { useState, useCallback, useEffect } from "react";
import { UploadCloud, X, Image as ImageIcon } from "lucide-react"; // External library icon

// Internal UI components
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useAuthorisation } from "../../hooks/useAuthorisation";

// Get the API URL from environment variables
const apiBaseUrl = process.env.REACT_APP_API || "/api";

function ImageUpload({ onImagesUploaded }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false); // Add uploading state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { getAccessTokenOrSignIn } = useAuthorisation();

  const processFiles = useCallback((selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    setFiles(fileArray);

    // Generate previews
    const newPreviews = fileArray.map((file) => URL.createObjectURL(file));
    setPreviews(newPreviews);
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      processFiles(event.target.files);
    },
    [processFiles]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles.length > 0) {
        processFiles(droppedFiles);
      }
    },
    [processFiles]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleRemoveImage = useCallback(
    (index) => {
      const newFiles = files.filter((_, i) => i !== index);
      const newPreviews = previews.filter((_, i) => i !== index);

      // Revoke the URL for the removed image
      URL.revokeObjectURL(previews[index]);

      setFiles(newFiles);
      setPreviews(newPreviews);
    },
    [files, previews]
  );

  // Cleanup object URLs on unmount or when files change
  useEffect(() => {
    return () => previews.forEach((url) => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previews]);

  const handleUploadClick = async () => {
    if (files.length === 0) return;

    setIsUploading(true); // Set uploading state
    setUploadProgress(0);
    console.log("Starting upload for files:", files);

    // Get the authentication token
    const accessToken = await getAccessTokenOrSignIn();
    if (!accessToken) {
      console.error("Failed to get authentication token");
      setIsUploading(false);
      return;
    }

    const uploadedImageIdentifiers = [];
    const totalFiles = files.length;

    // Upload each file individually to the real API
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("image", file);

      console.log(`Uploading ${file.name}...`);
      try {
        // Actual API call to upload the image
        const response = await fetch(`${apiBaseUrl}/secure/p2p/upload-image`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            // Don't set Content-Type with FormData, browser will set it with boundary
          },
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const result = await response.json();
        console.log(`Upload success for ${file.name}:`, result);

        if (result.success) {
          uploadedImageIdentifiers.push(result.imageId);
        } else {
          console.error(
            `Upload failed for ${file.name}:`,
            result?.error || "Unknown error"
          );
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        // Handle network errors etc.
      }

      // Update progress
      setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
    }

    console.log("Uploads complete. Identifiers:", uploadedImageIdentifiers);
    setIsUploading(false); // Clear uploading state
    setUploadProgress(0);

    // Pass the collected identifiers to the parent
    if (uploadedImageIdentifiers.length > 0) {
      onImagesUploaded(uploadedImageIdentifiers);
    } else {
      console.warn("No images were successfully uploaded.");
      // Optionally notify user of failure
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="border-2">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
              1
            </div>
            <div>
              <CardTitle className="text-2xl">Upload Item Photos</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add clear photos of your item from multiple angles
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Hidden file input */}
          <input
            id="image-upload"
            type="file"
            multiple
            accept="image/png, image/jpeg, image/heic"
            onChange={handleFileChange}
            disabled={isUploading}
            className="hidden"
          />

          {/* Drag and Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-all ${
              isDragging
                ? "border-blue-500 bg-blue-50 scale-[1.02]"
                : "border-gray-300 hover:border-gray-400"
            } ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            <div className="flex flex-col items-center gap-4">
              <div
                className={`rounded-full p-6 ${
                  isDragging ? "bg-blue-100" : "bg-gray-100"
                }`}
              >
                <UploadCloud
                  className={`w-16 h-16 ${
                    isDragging ? "text-blue-600" : "text-gray-400"
                  }`}
                />
              </div>

              <div>
                <p className="text-lg font-semibold mb-2">
                  {isDragging
                    ? "Drop your images here"
                    : "Drag and drop your images here"}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  or click the button below to browse
                </p>
              </div>

              <label
                htmlFor="image-upload"
                className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-11 px-8 cursor-pointer ${
                  isUploading
                    ? "opacity-50 cursor-not-allowed bg-gray-200 text-gray-500"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                <ImageIcon className="mr-2 h-5 w-5" />
                Choose Files
                {files.length > 0 && (
                  <span className="ml-2 bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold">
                    {files.length}
                  </span>
                )}
              </label>

              <p className="text-xs text-gray-400 mt-2">
                Accepts PNG, JPEG, HEIC (Max 10 images)
              </p>
            </div>
          </div>

          {/* Image Previews */}
          {previews.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">
                Selected Images ({previews.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {previews.map((src, index) => (
                  <div
                    key={index}
                    className="relative group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all"
                  >
                    <img
                      src={src}
                      alt={`Preview ${index + 1}`}
                      className="object-cover w-full h-40"
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      disabled={isUploading}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      aria-label="Remove image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-white text-xs font-medium truncate">
                        {files[index]?.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Uploading images...
                </p>
                <p className="text-sm font-semibold text-blue-600">
                  {uploadProgress}%
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-blue-600 h-3 transition-all duration-300 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <Button
            onClick={handleUploadClick}
            disabled={files.length === 0 || isUploading}
            size="lg"
            className="w-full mt-6 h-12 text-base"
          >
            {isUploading ? (
              <>
                <UploadCloud className="mr-2 h-5 w-5 animate-pulse" />
                Uploading & Identifying...
              </>
            ) : (
              <>
                <UploadCloud className="mr-2 h-5 w-5" />
                Upload & Identify Item
              </>
            )}
          </Button>

          {files.length > 0 && !isUploading && (
            <p className="text-center text-sm text-gray-500 mt-3">
              Our AI will analyze your photos and extract item details
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ImageUpload;
